import { Select } from 'radix-ui';
import {
  type CSSProperties,
  type ChangeEvent,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { type ListItemKeySelector } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import { type IBaseSelectProps } from '../BaseSelect';
import { IconWithLoadingState } from './components/IconWithLoadingState';
import {
  BaseSelectCustom,
  Container,
  Fieldset,
  IndicatorContainer,
  IndicatorContainerScrolling,
  Input,
  Item,
  ItemExpanded,
  ItemScrolling,
  ItemScrollingExpanded,
  ListContainer,
  Loading,
  LoadingContainer,
  SelectDataItemContainer,
  SelectedItemMirrorHost,
  SelectIcon,
  Viewport,
} from './styles';
import { Logger } from '@gbs-monorepo-packages/common';

export { selectFilterTruncationStyle } from './styles';

export interface ISelectFilterData extends Record<string, any> {
  id: number;
}

const getItemKey: ListItemKeySelector<
  ISelectFilterListData<ISelectFilterData>
> = (index, { data }) => data.at(index)?.id ?? `not-found-${index}`;

export type ISelectFilterValue<T extends ISelectFilterData> = T & {
  index?: number;
};

export interface ISelectFilterListData<T extends ISelectFilterData> {
  attribute: keyof T;
  data: T[];
  itemChild?: (props: ISelectFilterChildrenItemProps<T>) => JSX.Element;
  truncateOptionText?: boolean;
  value?: ISelectFilterValue<T> | null;
  selectedIcon?: ReactNode;
}

export interface ISelectFilterChildrenItemProps<
  T extends ISelectFilterData,
> extends ISelectFilterItemProps {
  data: T;
}

export interface ISelectFilterProps<T extends ISelectFilterData> extends Omit<
  IBaseSelectProps,
  'children' | 'defaultValue' | 'onValueChange' | 'value'
> {
  name?: string;
  attribute: keyof T;
  children?: (props: ISelectFilterChildrenItemProps<T>) => JSX.Element;
  data: T[];
  dataCy?: string;
  hasMore: boolean;
  itemHeight?: number;
  loading?: boolean;
  next: () => Promise<void> | void;
  onSearch?: (search: string) => Promise<void> | void;
  onValueChange?: (value: ISelectFilterValue<T>) => void;
  onOpenChange?: (open: boolean) => void;
  pageSize: number;
  searchPlaceholder?: string;
  value: ISelectFilterValue<T> | null;
  selectedIcon?: ReactNode;
  truncateOptionText?: boolean;
}

const delaySearch = 1000;
const defaultHeight = 30;
const FIX_HEIGHT = 55;

export const SelectFilter = <T extends ISelectFilterData>({
  attribute,
  children,
  data,
  name = 'select-filter',
  dataCy = 'select-filter',
  hasMore,
  icon = null,
  itemHeight = defaultHeight,
  loading = false,
  next,
  onOpenChange,
  onSearch,
  onValueChange,
  pageSize,
  searchPlaceholder = 'Search',
  value,
  selectedIcon,
  truncateOptionText = true,
  ...props
}: ISelectFilterProps<T>): JSX.Element => {
  const lastSearch = useRef('');
  const searchTimeOutHandler = useRef<NodeJS.Timeout>();

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const loadingNext = useRef(false);

  const handleSearch = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const newSearch = target.value.trimStart();
      if (newSearch.length <= 50) {
        clearTimeout(searchTimeOutHandler.current);
        if (!newSearch || lastSearch.current !== newSearch) {
          searchTimeOutHandler.current = setTimeout(() => {
            lastSearch.current = newSearch;
            void onSearch?.(newSearch.trimEnd());
          }, delaySearch);
        }
      }
    },
    [onSearch, lastSearch]
  );

  const handleOnKeyPressFocus = ({
    key,
  }: ReactKeyboardEvent<HTMLDivElement>) => {
    if (key === 'Backspace' || /^([A-Za-z0-9_ ])$/.test(key)) {
      inputRef.current?.focus();
    }
  };

  const handleSelectOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        inputRef.current?.focus();
      } else {
        const clearSearch = '';

        clearTimeout(searchTimeOutHandler.current);
        if (lastSearch.current !== clearSearch) {
          lastSearch.current = clearSearch;
          void onSearch?.(clearSearch.trimEnd());
        }
      }
      onOpenChange?.(open);
    },
    [onOpenChange, onSearch]
  );

  const loadMoreItems = useCallback(
    async (_startIndex: number, _stopIndex: number) => {
      if (loadingNext.current) {
        return;
      }
      loadingNext.current = true;
      const scrollTopBeforeNext = scrollElementRef.current?.scrollTop ?? 0;
      try {
        await next();
        if (scrollTopBeforeNext > 0) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (scrollElementRef.current) {
                scrollElementRef.current.scrollTop = scrollTopBeforeNext;
              }
            });
          });
        }
      } catch (error) {
        Logger.error(`Error loading more items: ${error}`);
      } finally {
        loadingNext.current = false;
      }
    },
    [next]
  );

  const displayData = useMemo((): T[] => {
    if (value === null || value === undefined) {
      return data;
    }
    const idx = data.findIndex((item) => item.id === value.id);
    if (idx === -1) {
      return [value as T, ...data];
    }
    if (idx === 0) {
      return data;
    }
    const copy = [...data];
    const [picked] = copy.splice(idx, 1);
    return [picked, ...copy];
  }, [data, value]);

  const handleValueChange = useCallback(
    (nextValue: string) => {
      const id = parseInt(nextValue, 10);
      const indexInSource = data.findIndex((item) => item.id === id);
      if (indexInSource !== -1) {
        onValueChange?.({
          ...data[indexInSource],
          index: indexInSource,
        });
        return;
      }
      const row = displayData.find((item) => item.id === id);
      if (row) {
        onValueChange?.({ ...row });
      }
    },
    [data, displayData, onValueChange]
  );

  const dataLength = hasMore ? displayData.length + 1 : displayData.length;
  const listMaxHeight = itemHeight * pageSize;
  const listHeight = Math.min(listMaxHeight, itemHeight * displayData.length);
  const isItemLoaded = (index: number) =>
    !hasMore || index < displayData.length;

  // FIXME(PFM-640): This is a workaround to avoid a type error
  // eslint-disable-next-line `@typescript-eslint/consistent-type-assertions`
  const itemData = useMemo(
    () =>
      ({
        attribute,
        data: displayData,
        itemChild: children,
        truncateOptionText,
        value,
        selectedIcon,
      }) as ISelectFilterListData<ISelectFilterData>,
    [attribute, displayData, children, truncateOptionText, value, selectedIcon]
  );

  const selectedDataIndex = useMemo(() => {
    if (value === null || value === undefined) {
      return -1;
    }
    return displayData.findIndex((item) => item.id === value.id);
  }, [displayData, value]);

  const selectedItemMirrorData = useMemo(() => {
    if (value === null || value === undefined) {
      return null;
    }
    return (displayData.find((item) => item.id === value.id) ?? value) as T;
  }, [displayData, value]);

  const valueDisplayTextForTrigger = useMemo(() => {
    if (value === null || value === undefined) {
      return null;
    }
    return attribute in value ? String(value[attribute]) : '-';
  }, [attribute, value]);

  const renderInnerElement = useCallback(
    (innerProps: IInnerSelectProps) => (
      <InnerSelectElement {...innerProps} dataCy={dataCy} />
    ),
    [dataCy]
  );

  return (
    <Container data-cy={`${dataCy}-container`}>
      <BaseSelectCustom
        {...props}
        name={name}
        dataCy={`${dataCy}`}
        onKeyPress={handleOnKeyPressFocus}
        onOpenChange={handleSelectOpenChange}
        onValueChange={handleValueChange}
        overwritePortalHeight={listHeight + FIX_HEIGHT}
        value={
          value === undefined || value === null ? null : value.id.toString()
        }
        valueDisplayText={valueDisplayTextForTrigger ?? undefined}
        icon={<IconWithLoadingState icon={icon} loading={loading} />}
      >
        <Fieldset>
          <Input
            id={`${name}-search`}
            data-cy={`input-${dataCy}`}
            onChange={handleSearch}
            placeholder={searchPlaceholder}
            ref={inputRef}
            type="text"
          />
        </Fieldset>

        <Viewport data-cy="select-content-container">
          {selectedItemMirrorData !== null && (
            <SelectedItemMirrorHost data-cy={`${dataCy}-selected-mirror`}>
              {/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- mirror row matches list typing */}
              <SelectFilterListItem
                attribute={attribute as keyof ISelectFilterData}
                data={selectedItemMirrorData as ISelectFilterData}
                dataCy={`${dataCy}-item-mirror`}
                itemHeight={itemHeight}
                selectedIcon={selectedIcon}
                truncateOptionText={truncateOptionText}
              >
                {children as ISelectFilterProps<ISelectFilterData>['children']}
              </SelectFilterListItem>
            </SelectedItemMirrorHost>
          )}
          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={dataLength}
            loadMoreItems={loadMoreItems}
          >
            {({ onItemsRendered, ref }) => (
              <ListContainer
                height={listHeight}
                innerElementType={renderInnerElement}
                itemCount={dataLength}
                itemData={itemData}
                itemKey={getItemKey}
                itemSize={itemHeight}
                onItemsRendered={onItemsRendered}
                outerRef={scrollElementRef}
                ref={ref}
                width="100%"
              >
                {({
                  data: {
                    attribute,
                    data,
                    itemChild,
                    selectedIcon,
                    truncateOptionText: shouldTruncateOptionText = true,
                  },
                  index,
                  style,
                }) => {
                  const suppressSelectItem =
                    selectedDataIndex !== -1 && index === selectedDataIndex;

                  return (
                    <SelectFilterListItem
                      attribute={attribute}
                      data={data.at(index)}
                      dataCy={dataCy}
                      itemHeight={itemHeight}
                      itemStyle={style}
                      selectedIcon={selectedIcon}
                      suppressSelectItem={suppressSelectItem}
                      truncateOptionText={shouldTruncateOptionText}
                    >
                      {itemChild}
                    </SelectFilterListItem>
                  );
                }}
              </ListContainer>
            )}
          </InfiniteLoader>
        </Viewport>
      </BaseSelectCustom>
    </Container>
  );
};

export interface ISelectFilterItemProps
  extends HTMLAttributes<HTMLDivElement>, Select.SelectItemProps {
  dataCy: string;
  text: string;
  selectedIcon?: ReactNode;
  truncateOptionText?: boolean;
}

export const SelectFilterItem = forwardRef<
  HTMLDivElement,
  ISelectFilterItemProps
>(
  // eslint-disable-next-line no-restricted-syntax
  function SelectFilterItem(
    {
      dataCy = 'select-filter-item',
      text,
      truncateOptionText = true,
      value,
      selectedIcon,
      ...props
    },
    ref
  ): JSX.Element {
    const OptionItem = truncateOptionText ? Item : ItemExpanded;

    return (
      <OptionItem
        {...props}
        data-cy={`option-${dataCy.toLowerCase()}`}
        value={value}
        ref={ref}
      >
        <Select.ItemText data-cy="item-text">{text}</Select.ItemText>
        <IndicatorContainer data-cy="item-selected-indicator">
          {selectedIcon ?? <SelectIcon />}
        </IndicatorContainer>
      </OptionItem>
    );
  }
);

interface ISelectFilterItemVisualProps extends HTMLAttributes<HTMLDivElement> {
  dataCy: string;
  selectedIcon?: ReactNode;
  text: string;
  truncateOptionText?: boolean;
}

const SelectFilterItemVisual = forwardRef<
  HTMLDivElement,
  ISelectFilterItemVisualProps
>(
  // eslint-disable-next-line no-restricted-syntax
  function SelectFilterItemVisual(
    {
      dataCy = 'select-filter-item-visual',
      text,
      truncateOptionText = true,
      selectedIcon,
      ...props
    },
    ref
  ): JSX.Element {
    const OptionItemVisual = truncateOptionText
      ? ItemScrolling
      : ItemScrollingExpanded;

    return (
      <OptionItemVisual {...props} aria-hidden data-cy={dataCy} ref={ref}>
        <span data-cy="item-text">{text}</span>
        <IndicatorContainerScrolling data-cy="item-selected-indicator">
          {selectedIcon ?? <SelectIcon />}
        </IndicatorContainerScrolling>
      </OptionItemVisual>
    );
  }
);

export interface ISelectFilterListItemProps
  extends
    Omit<HTMLAttributes<HTMLDivElement>, 'children'>,
    Omit<ISelectFilterItemProps, 'children' | 'text' | 'value'> {
  attribute: keyof ISelectFilterData;
  children?: (
    props: ISelectFilterChildrenItemProps<ISelectFilterData>
  ) => JSX.Element;
  data?: ISelectFilterData;
  dataCy: string;
  itemHeight: number;
  itemOffset?: number;
  itemStyle?: CSSProperties;
  selectedIcon?: ReactNode;
  suppressSelectItem?: boolean;
  truncateOptionText?: boolean;
}

export const SelectFilterListItem = forwardRef<
  HTMLDivElement,
  ISelectFilterListItemProps
>(
  // eslint-disable-next-line no-restricted-syntax
  function SelectFilterListItem(
    {
      attribute,
      children,
      data = null,
      dataCy,
      itemHeight,
      itemOffset = 0,
      itemStyle: { top = 0, ...style } = {},
      selectedIcon,
      suppressSelectItem = false,
      truncateOptionText = true,
      ...props
    },
    ref
  ): JSX.Element {
    if (!data) {
      return (
        <LoadingContainer
          {...props}
          $itemOffset={itemOffset}
          $top={top}
          data-cy={`${dataCy}-loading`}
          ref={ref}
          style={style}
        >
          <Loading $height={itemHeight} $width={itemHeight} />
        </LoadingContainer>
      );
    }
    const value = data.id.toString();

    const text = attribute in data ? String(data[attribute]) : '-';

    return (
      <SelectDataItemContainer
        {...props}
        $itemOffset={itemOffset}
        $top={top}
        data-cy={`${dataCy}-item-${value}`}
        ref={ref}
        style={style}
      >
        {suppressSelectItem ? (
          <SelectFilterItemVisual
            dataCy={`option-${text.toLocaleLowerCase()}-visual`}
            text={text}
            selectedIcon={selectedIcon}
            truncateOptionText={truncateOptionText}
          />
        ) : (
          (children?.({
            value,
            dataCy: text,
            text,
            data,
          }) ?? (
            <SelectFilterItem
              value={value}
              dataCy={text.toLocaleLowerCase()}
              text={text}
              selectedIcon={selectedIcon}
              truncateOptionText={truncateOptionText}
            />
          ))
        )}
      </SelectDataItemContainer>
    );
  }
);

interface IInnerSelectProps {
  children: ReactNode;
  dataCy?: string;
  style?: CSSProperties;
}

const InnerSelectElement = forwardRef<HTMLDivElement, IInnerSelectProps>(
  // eslint-disable-next-line no-restricted-syntax
  function InnerSelect(
    { children, dataCy = 'select-filter', ...props },
    ref
  ): JSX.Element {
    return (
      <div data-cy={`${dataCy.toLocaleLowerCase()}-inner`} ref={ref} {...props}>
        {children}
      </div>
    );
  }
);
